import { Component, OnInit, NgZone, OnDestroy } from "@angular/core";

import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { HttpClient } from "@angular/common/http";

import { ToastrService } from "ngx-toastr";
import { environment } from "src/environments/environment";
import { Router } from "@angular/router";
import { countries } from "src/app/shared/constants";
import { DataService, TextDocumentMapping } from "src/app/shared/dataService";

@Component({
  selector: "app-contact-form-only",
  templateUrl: "./contact-form-only.component.html",
  styleUrls: ["./contact-form-only.component.scss"],
})
export class ContactFormOnlyComponent implements OnInit, OnDestroy {
  public contactForm: FormGroup;
  public isLoading = false;
  public countries = countries;
  private apiUrl = `${environment._api_base_url}/contact-forms`;
  public readonly siteKey = environment.recaptcha_site_key;
  public captchaSuccess = false;
  public captchaToken: string = "";
  private widgetId: number = null;
  private recaptchaLoadedListener: () => void;
  public TextDocumentMapping = TextDocumentMapping;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private dataService: DataService,
    private toastr: ToastrService,
    private router: Router,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.contactForm = this.fb.group({
      firstName: ["", Validators.required],
      lastName: ["", Validators.required],
      company: ["", Validators.required],
      phone: ["", [Validators.pattern("^[0-9]*$"), Validators.minLength(8)]],
      email: ["", [Validators.required, Validators.email]],
      website: [""],
      referredBy: [""],
      comments: [""],
      country: ["", Validators.required],
    });

    // Setup reCAPTCHA
    this.setupReCaptcha();
  }

  ngOnDestroy(): void {
    // Clean up global functions and event listeners
    if ((window as any).handleRecaptchaSuccess) {
      (window as any).handleRecaptchaSuccess = undefined;
    }

    if ((window as any).handleRecaptchaExpire) {
      (window as any).handleRecaptchaExpire = undefined;
    }

    if (this.recaptchaLoadedListener) {
      window.removeEventListener(
        "recaptchaLoaded",
        this.recaptchaLoadedListener
      );
    }
  }

  private setupReCaptcha(): void {
    // Define the callbacks for reCAPTCHA
    (window as any).handleRecaptchaSuccess = (token: string) => {
      this.ngZone.run(() => {
        this.captchaSuccess = true;
        this.captchaToken = token;
      });
    };

    (window as any).handleRecaptchaExpire = () => {
      this.ngZone.run(() => {
        this.captchaSuccess = false;
        this.captchaToken = "";
      });
    };

    // Check if grecaptcha is already loaded
    if ((window as any).recaptchaLoaded && (window as any).grecaptcha) {
      this.renderReCaptcha();
    } else {
      // Add event listener for when reCAPTCHA loads
      this.recaptchaLoadedListener = () => {
        this.renderReCaptcha();
      };
      window.addEventListener("recaptchaLoaded", this.recaptchaLoadedListener);
    }
  }

  private renderReCaptcha(): void {
    this.ngZone.run(() => {
      if (
        !(window as any).grecaptcha ||
        !(window as any).grecaptcha.enterprise
      ) {
        console.error("reCAPTCHA not loaded yet");
        return;
      }

      // Find the container element
      const container = document.getElementById(
        "catpcha-container"
      ) as HTMLElement;
      if (!container) {
        console.error("reCAPTCHA container not found");
        return;
      }

      // Render the reCAPTCHA widget
      try {
        this.widgetId = (window as any).grecaptcha.enterprise.render(
          container,
          {
            sitekey: this.siteKey,
            action: "markitplace_contact_form",
            callback: "handleRecaptchaSuccess",
            "expired-callback": "handleRecaptchaExpire",
          }
        );
      } catch (error) {
        console.error("Error rendering reCAPTCHA:", error);
      }
    });
  }

  private resetReCaptcha(): void {
    if (
      this.widgetId !== null &&
      (window as any).grecaptcha &&
      (window as any).grecaptcha.enterprise
    ) {
      (window as any).grecaptcha.enterprise.reset(this.widgetId);
      this.captchaSuccess = false;
      this.captchaToken = "";
    }
  }

  markFormGroupTouched(formGroup: FormGroup) {
    if (formGroup.invalid) {
      Object.keys(formGroup.controls).forEach((key) => {
        const control = formGroup.get(key);
        control.markAsTouched();

        if (control instanceof FormGroup) {
          this.markFormGroupTouched(control);
        }
      });
    }
  }

  onSubmit(): void {
    this.markFormGroupTouched(this.contactForm);
    if (this.contactForm.valid) {
      if (this.captchaSuccess === false) {
        this.toastr.warning(
          "Please complete the reCAPTCHA to proceed.",
          "Error"
        );
        return;
      }
      this.isLoading = true;
      this.dataService
        .validateCaptchaToken(this.captchaToken, "markitplace_contact_form")
        .subscribe(
          (response) => {
            if (response.score >= 0.7) {
              this.captchaSuccess = true;

              this.http
                .post(this.apiUrl, {
                  contactFormData: this.contactForm.value,
                })
                .subscribe(
                  () => {
                    this.router.navigate(["/thanks"], {
                      queryParams: { id: "marketplace-contact" },
                    });
                  },
                  (error) => {
                    this.toastr.error(
                      "There was an error submitting the form. Please try again later.",
                      "Error"
                    );
                    console.error("Error submitting form:", error);
                    this.isLoading = false;
                    // Reset reCAPTCHA if form submission fails
                    this.resetReCaptcha();
                  }
                );
            } else {
              this.captchaSuccess = false;
              this.toastr.error(
                "Recaptcha validation failed. Please try again.",
                "Error"
              );
              this.isLoading = false;
              // Reset reCAPTCHA when score is too low
              this.resetReCaptcha();
            }
          },
          (error) => {
            console.error("Error validating recaptcha:", error);
            this.captchaSuccess = false;
            this.toastr.error(
              "There was an error validating the recaptcha. Please try again.",
              "Error"
            );
            this.isLoading = false;
            // Reset reCAPTCHA when validation error occurs
            this.resetReCaptcha();
          }
        );
    } else {
      this.toastr.warning(
        "Please provide required information and check there are no errors in the form to proceed.",
        "Error"
      );
    }
  }
}
